import { type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { LayoutGrid } from 'lucide-react';

import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  usePrimaryNavItems,
  isPrimaryNavItemActive,
} from '@/hooks/usePrimaryNav';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { PHASE1_FLAGS } from '@/lib/phase1Paths';
import { Phase1Rail } from '@/components/sidebar/Phase1Rail';

/**
 * `inert` on a closed sheet, spread rather than passed directly: React 18 has no
 * type for the attribute (React 19 added the boolean prop), and it must be
 * absent rather than `false` — any present value makes the subtree inert.
 */
function inertWhenClosed(open: boolean): Record<string, unknown> {
  return open ? {} : { inert: '' };
}

interface MobileTabBarProps {
  /**
   * The "More" sheet's body — the current module's second-level navigation.
   * Receives a `close` callback so a row can dismiss the sheet after it
   * navigates.
   */
  children?: (close: () => void) => ReactNode;
  /** Pinned to the bottom of the More sheet (the user menu). */
  footer?: ReactNode;
  /**
   * Changes whenever the host navigates in a way the router does not see —
   * picking a thread, say. The sheet closes when it does. Without this the
   * only alternative is closing on any click inside, which also eats taps on
   * the sheet's own tabs and search.
   */
  closeKey?: string;
}

/**
 * MobileTabBar — the phone shell from the v9 design
 * (migration-docs/v9/worldvia-platform-prototype.html).
 *
 * Below `md` the sidebars leave the flow and navigation becomes a bottom tab
 * bar: the primary destinations, plus a More tab whose bottom sheet carries the
 * module's second level and the user menu. This replaces the off-canvas
 * hamburger drawer.
 *
 * Two details are lifted straight from v9 rather than reinvented:
 *
 * - The bar is a `flex-none` child of the layout column, NOT `position: fixed`.
 *   The content row simply gets 56px less to work with, so nothing has to
 *   reserve space for it and no `vh` maths is involved.
 * - The active tab wears a 2px waypoint on the bar's top edge — the same marker
 *   the desktop rail uses, turned to the bar's edge.
 */
export function MobileTabBar({ children, footer, closeKey }: MobileTabBarProps) {
  const [location, navigate] = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const items = usePrimaryNavItems();

  // A tap that navigates must not leave the sheet standing — whether the
  // router moved or the host told us it did.
  useEffect(() => {
    setMoreOpen(false);
  }, [location, closeKey]);

  const close = () => setMoreOpen(false);

  // Escape closes the sheet — a plain panel gets none of that for free.
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);
  // AR-445 — on the Phase-1 navigation the tab bar carries four modules, so the
  // More sheet carries every row, whichever layout is showing it.
  const { isEnabled, isLoading } = useResolvedFeatureFlags();
  const phase1On = !isLoading && PHASE1_FLAGS.every((f) => isEnabled(f));
  const hasMore = Boolean(children || footer || phase1On);

  const tabClass = (active: boolean, disabled?: boolean) =>
    cn(
      'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-[3px]',
      'px-1 text-[11.5px] font-semibold leading-none transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
      disabled
        ? 'cursor-not-allowed text-gray-300 dark:text-gray-600'
        : active
          ? 'text-orange-600 dark:text-orange-400'
          : 'text-gray-500 dark:text-gray-400',
    );

  /* the waypoint the active tab wears, on the bar's top edge */
  const waypoint = (
    <span
      aria-hidden
      className="absolute inset-x-[16%] top-0 h-0.5 rounded-b-full bg-orange-500"
    />
  );

  return (
    <>
      <nav
        data-app-chrome
        data-testid="mobile-tab-bar"
        aria-label="Primary"
        className={cn(
          /* above the sheet overlay, so the bar stays visible AND clickable
             while the More sheet is open — v9 keeps level 1 reachable */
          'relative z-[60] flex h-14 flex-none items-stretch md:hidden',
          'border-t border-gray-200 bg-background dark:border-gray-700',
          'shadow-[0_-2px_10px_rgba(15,23,42,0.06)]',
        )}
      >
        {items.map((item) => {
          const active = isPrimaryNavItemActive(item, location);
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              type="button"
              disabled={item.disabled}
              aria-current={active ? 'page' : undefined}
              onClick={() => {
                if (item.disabled || !item.path) return;
                // Tapping the module you are already in opens its second level (on
                // Threads, the thread list) instead of doing nothing.
                if (active && children) {
                  setMoreOpen(true);
                  return;
                }
                navigate(item.path);
              }}
              className={tabClass(active, item.disabled)}
            >
              {active && waypoint}
              <Icon className="h-5 w-5" />
              <span className="max-w-full truncate">
                {item.shortLabel ?? item.label}
              </span>
            </button>
          );
        })}

        {hasMore && (
          <button
            type="button"
            data-testid="mobile-tab-more"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(true)}
            className={tabClass(moreOpen)}
          >
            {moreOpen && waypoint}
            <LayoutGrid className="h-5 w-5" />
            <span>More</span>
          </button>
        )}
      </nav>

      {/* The More sheet.
          Deliberately NOT a modal dialog: v9's sheet is a plain positioned
          panel that slides up to sit ON TOP OF the content but BELOW the tab
          bar, so level 1 stays reachable the whole time. A Radix Dialog would
          take pointer events off the body and leave the bar looking enabled
          while doing nothing — the exact thing v9's own law forbids. */}
      {hasMore && (
        <>
          <div
            aria-hidden
            onClick={close}
            className={cn(
              'fixed inset-0 z-40 bg-gray-900/25 transition-opacity duration-200 md:hidden',
              moreOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          />
          <div
            role="dialog"
            aria-label="More"
            aria-hidden={!moreOpen}
            data-testid="mobile-more-sheet"
            {...inertWhenClosed(moreOpen)}
            className={cn(
              'fixed inset-x-0 bottom-14 z-50 max-h-[60%] overflow-y-auto md:hidden',
              'rounded-t-2xl border-t border-gray-200 bg-background dark:border-gray-700',
              'shadow-[0_-12px_34px_rgba(15,23,42,0.20)]',
              !moreOpen && 'pointer-events-none',
            )}
            /* Inline rather than utilities, for two reasons.

               The offset: a Tailwind translate sets --tw-translate-y, which the
               sheet's own transform utilities then re-compose — the value stuck
               at 102% even with translate-y-0 applied. v9 toggles transform
               directly too.

               `visibility`: off-screen is not the same as gone. A translated
               sheet keeps its Close button and every control the host passes in
               inside the tab order, so a keyboard user tabs into an invisible
               panel. `visibility: hidden` takes the whole subtree out of it in
               every browser, and the 200ms delay on the way out lets the sheet
               finish sliding before it disappears. `inert` above does the same
               for browsers that have it, and also excludes the subtree from
               find-in-page. */
            style={{
              transform: moreOpen ? 'none' : 'translateY(102%)',
              visibility: moreOpen ? 'visible' : 'hidden',
              transition: moreOpen
                ? 'transform 200ms ease-out, visibility 0s'
                : 'transform 200ms ease-out, visibility 0s linear 200ms',
            }}
          >
            {/* the sheet's own chrome — a row of its own, so a child's header
                controls can never end up underneath the close button */}
            <div className="relative flex items-center justify-end px-2 pt-2">
              <div
                aria-hidden
                className="absolute left-1/2 top-3 h-1 w-9 -translate-x-1/2 rounded-full bg-gray-200 dark:bg-gray-700"
              />
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="inline-flex h-11 w-11 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-col pb-2">
              {children?.(close)}
              {phase1On ? (
                <div className={cn(children && 'mt-2 border-t border-gray-200 dark:border-gray-700')}>
                  <Phase1Rail inDrawer onNavigated={close} showUserMenu={false} />
                </div>
              ) : null}
              {footer ? (
                <div className="mt-auto border-t border-gray-200 px-3 py-3 dark:border-gray-700">
                  {footer}
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default MobileTabBar;
